import "./DataGrid.css";
import { DataGridAutocompleteEditor } from "./DataGridAutocompleteEditor.jsx";
import { DataGridComboboxEditor } from "./DataGridComboboxEditor.jsx";
import { useDataGridState } from "./useDataGridState.js";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";

function stepNumericInputValue(rawValue, direction, { min, max } = {}) {
  const currentValue = Number(rawValue);
  const safeValue = Number.isFinite(currentValue) ? currentValue : 0;
  const nextValue = safeValue + direction;
  const minValue = Number(min);
  const maxValue = Number(max);
  const clampedMin = Number.isFinite(minValue) ? Math.max(nextValue, minValue) : nextValue;
  const clampedValue = Number.isFinite(maxValue) ? Math.min(clampedMin, maxValue) : clampedMin;

  return String(clampedValue);
}

function buildHeaderRows(columns, headerGroups, showGroupedLeafHeaders) {
  if (!headerGroups?.length) {
    return {
      hasGroupedHeaders: false,
      topRow: null,
      bottomRow: columns.map((column) => ({
        key: column.key,
        label: column.label,
        align: column.align,
        colSpan: 1,
        rowSpan: 1,
        scope: "col",
        sortable: column.sortable !== false
      }))
    };
  }

  const columnKeys = new Set(columns.map((column) => column.key));
  const groupedColumnKeys = headerGroups.flatMap((group) => group.columns);

  if (groupedColumnKeys.length !== columns.length) {
    throw new Error("DataGrid headerGroups must cover every column exactly once.");
  }

  if (new Set(groupedColumnKeys).size !== groupedColumnKeys.length) {
    throw new Error("DataGrid headerGroups cannot repeat columns.");
  }

  if (!groupedColumnKeys.every((columnKey) => columnKeys.has(columnKey))) {
    throw new Error("DataGrid headerGroups references an unknown column.");
  }

  const columnsByKey = new Map(columns.map((column) => [column.key, column]));
  const bottomRow = showGroupedLeafHeaders
    ? headerGroups.flatMap((group) =>
      group.columns.map((columnKey) => {
        const column = columnsByKey.get(columnKey);

        return {
          key: column.key,
          label: group.columnLabels?.[columnKey] ?? column.label,
          align: column.align,
          colSpan: 1,
          rowSpan: 1,
          scope: "col",
          sortable: column.sortable !== false
        };
      })
    )
    : null;

  return {
    hasGroupedHeaders: true,
    topRow: headerGroups.map((group) => ({
      key: group.key,
      label: group.label,
      align: group.columns.length === 1 ? columnsByKey.get(group.columns[0])?.align : "text",
      colSpan: group.columns.length,
      rowSpan: 1,
      scope: bottomRow ? "colgroup" : "col",
      sortable: bottomRow ? false : (group.sortable !== false),
      sortKey: group.sortKey ?? (group.columns.length === 1 ? group.columns[0] : null)
    })),
    bottomRow
  };
}

function buildFocusContextSnapshot({
  row,
  column,
  rowIndex,
  colIndex,
  editing,
  focused
}) {
  return JSON.stringify({
    rowId: row?.id ?? null,
    columnKey: column?.key ?? null,
    rowIndex,
    colIndex,
    editing: Boolean(editing),
    focused: Boolean(focused)
  });
}

function buildGridFocusContext(gridRef, columns, rows, selection, editing, focused) {
  const row = rows[selection.row];
  const column = columns[selection.col];
  const cellElement = gridRef.current?.querySelector(
    `[data-row-index="${selection.row}"][data-col-index="${selection.col}"]`
  ) ?? null;

  return {
    row,
    column,
    rowIndex: selection.row,
    colIndex: selection.col,
    cellElement,
    editing: Boolean(editing),
    focused
  };
}

export function DataGrid({
  columns,
  rows,
  onRowsChange,
  headerGroups,
  showGroupedLeafHeaders = true,
  rowEditing,
  sortState,
  onSortChange,
  onApiReady,
  onSelectionChange,
  onFocusContextChange,
  showHeaders = true,
  embedded = false,
  shortcuts = {}
}) {
  const draftRowRef = useRef(null);
  const previousSortStateRef = useRef(sortState);
  const focusContextSnapshotRef = useRef("");
  const [, setDraftRowVersion] = useState(0);
  const firstEditableColumnIndex = useMemo(
    () => columns.findIndex((column) => column.editable),
    [columns]
  );

  let displayRows = rows;
  let draftRowIndex = -1;
  let appendedRowId = null;

  if (rowEditing?.appendEmptyRow) {
    const existingDraftRowIndex = rows.findIndex((row) => !rowEditing.isRowComplete(row));
    if (existingDraftRowIndex >= 0) {
      draftRowRef.current = null;
      const existingDraftRow = rows[existingDraftRowIndex];
      displayRows = existingDraftRowIndex === 0
        ? rows
        : [existingDraftRow, ...rows.filter((_, index) => index !== existingDraftRowIndex)];
      draftRowIndex = 0;
    } else {
      if (!draftRowRef.current) {
        draftRowRef.current = rowEditing.createEmptyRow();
      }

      displayRows = [
        draftRowRef.current,
        ...rows
      ];
      draftRowIndex = 0;
      appendedRowId = draftRowRef.current?.id ?? null;
    }
  }

  function handleDraftRowChange(nextDraftRow) {
    draftRowRef.current = nextDraftRow;
    setDraftRowVersion((current) => current + 1);
  }

  const grid = useDataGridState({
    columns,
    rows: displayRows,
    rowCount: displayRows.length,
    onRowsChange,
    onDraftRowChange: handleDraftRowChange,
    rowEditing,
    appendedRowId,
    firstEditableColumnIndex,
    shortcuts
  });
  const headers = buildHeaderRows(columns, headerGroups, showGroupedLeafHeaders);
  const [hasGridFocus, setHasGridFocus] = useState(false);

  function toggleSort(columnKey) {
    if (!onSortChange) {
      return;
    }

    if (sortState?.key !== columnKey) {
      onSortChange({ key: columnKey, direction: "asc" });
      return;
    }

    if (sortState.direction === "asc") {
      onSortChange({ key: columnKey, direction: "desc" });
      return;
    }

    onSortChange(null);
  }

  useEffect(() => {
    if (!onApiReady) {
      return undefined;
    }

    const firstEditableCol = firstEditableColumnIndex >= 0 ? firstEditableColumnIndex : 0;

    onApiReady({
      focusGrid() {
        grid.gridRef.current?.focus({ preventScroll: true });
      },
      restoreSelection(selection = {}) {
        const { rowId = null, columnKey = null, focus = true } = selection;
        if (displayRows.length === 0) {
          return;
        }

        const targetRowIndex = rowId
          ? displayRows.findIndex((row) => row?.id === rowId)
          : -1;
        const resolvedRowIndex = targetRowIndex >= 0
          ? targetRowIndex
          : (draftRowIndex >= 0 ? draftRowIndex : 0);
        const targetColIndex = columnKey
          ? columns.findIndex((column) => column.key === columnKey)
          : -1;
        const resolvedColIndex = targetColIndex >= 0
          ? targetColIndex
          : (firstEditableCol >= 0 ? firstEditableCol : 0);

        grid.moveSelectionTo(resolvedRowIndex, resolvedColIndex);
        if (focus) {
          grid.gridRef.current?.focus({ preventScroll: true });
        }
      },
      focusDraftRow() {
        if (draftRowIndex < 0) {
          return;
        }

        grid.moveSelectionTo(draftRowIndex, firstEditableCol);
        grid.activateCell(draftRowIndex, firstEditableCol);
      },
      startDraftRowEdit() {
        if (draftRowIndex < 0) {
          return;
        }

        const targetRow = displayRows[draftRowIndex];
        grid.activateCell(draftRowIndex, firstEditableCol);
        grid.startEdit(draftRowIndex, firstEditableCol, targetRow);
      },
      duplicateSelectedRow() {
        grid.duplicateSelectedRow();
      }
    });

    return () => {
      onApiReady(null);
    };
  }, [columns, displayRows, draftRowIndex, firstEditableColumnIndex, grid, onApiReady]);

  useEffect(() => {
    if (!onSelectionChange) {
      return;
    }

    onSelectionChange(buildGridFocusContext(
      grid.gridRef,
      columns,
      displayRows,
      grid.selectedCell,
      grid.editingCell,
      hasGridFocus
    ));
  }, [columns, displayRows, grid.editingCell, grid.gridRef, grid.selectedCell, hasGridFocus, onSelectionChange]);

  useEffect(() => {
    if (!onFocusContextChange) {
      return;
    }

    const nextFocusContext = buildGridFocusContext(
      grid.gridRef,
      columns,
      displayRows,
      grid.selectedCell,
      grid.editingCell,
      hasGridFocus
    );
    const snapshot = buildFocusContextSnapshot(nextFocusContext);
    if (focusContextSnapshotRef.current === snapshot) {
      return;
    }

    focusContextSnapshotRef.current = snapshot;
    onFocusContextChange(nextFocusContext);
  }, [
    columns,
    displayRows,
    grid.editingCell,
    grid.gridRef,
    grid.selectedCell,
    hasGridFocus,
    onFocusContextChange
  ]);

  useEffect(() => {
    const previousSortState = previousSortStateRef.current;
    previousSortStateRef.current = sortState;

    if (previousSortState === sortState) {
      return;
    }

    const previousKey = previousSortState?.key ?? null;
    const previousDirection = previousSortState?.direction ?? null;
    const nextKey = sortState?.key ?? null;
    const nextDirection = sortState?.direction ?? null;

    if (previousKey === nextKey && previousDirection === nextDirection) {
      return;
    }

    if (displayRows.length === 0) {
      return;
    }

    const targetRowIndex = draftRowIndex >= 0 ? draftRowIndex : 0;
    const targetColIndex = grid.selectedCell?.col ?? 0;
    grid.activateCell(targetRowIndex, targetColIndex);
  }, [displayRows.length, draftRowIndex, grid, sortState]);

  useEffect(() => {
    const gridElement = grid.gridRef.current;
    if (!gridElement) {
      return;
    }

    const groupHeaderRow = gridElement.querySelector(".header-row-group");
    if (!groupHeaderRow) {
      gridElement.style.setProperty("--data-grid-group-header-height", "0px");
      return;
    }

    const updateGroupHeaderHeight = () => {
      gridElement.style.setProperty(
        "--data-grid-group-header-height",
        `${groupHeaderRow.getBoundingClientRect().height}px`
      );
    };

    updateGroupHeaderHeight();

    const resizeObserver = new ResizeObserver(updateGroupHeaderHeight);
    resizeObserver.observe(groupHeaderRow);

    return () => {
      resizeObserver.disconnect();
    };
  }, [grid.gridRef, headers.hasGroupedHeaders]);

  return (
    <div
      class={`data-grid${embedded ? " data-grid--embedded" : ""}`}
      tabIndex={0}
      data-role="data-grid"
      ref={grid.gridRef}
      onFocusIn={() => {
        setHasGridFocus(true);
      }}
      onFocusOut={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) {
          return;
        }

        setHasGridFocus(false);
      }}
    >
      <table class="table">
        {showHeaders ? (
          <thead>
            {headers.topRow && (
              <tr class="header-row header-row-group">
                {headers.topRow.map((header) => (
                  <th
                    class={[
                      "header-cell",
                      headers.bottomRow ? "header-cell-group" : "",
                      header.sortable ? "sortable" : "",
                      header.align ?? "text"
                    ].filter(Boolean).join(" ")}
                    colSpan={header.colSpan}
                    rowSpan={header.rowSpan}
                    scope={header.scope}
                    key={header.key}
                    onClick={header.sortable && header.sortKey ? () => toggleSort(header.sortKey) : undefined}
                  >
                    <span class="header-label">{header.label}</span>
                    {header.sortKey && sortState?.key === header.sortKey && sortState.direction ? (
                      <span class="sort-indicator">
                        {sortState.direction === "asc" ? "▲" : "▼"}
                      </span>
                    ) : null}
                  </th>
                ))}
              </tr>
            )}
            {headers.bottomRow ? (
              <tr class={`header-row ${headers.hasGroupedHeaders ? "header-row-leaf" : ""}`}>
                {headers.bottomRow.map((header) => (
                  <th
                    class={[
                      "header-cell",
                      headers.hasGroupedHeaders ? "header-cell-leaf" : "",
                      header.sortable ? "sortable" : "",
                      header.align ?? "text"
                    ].filter(Boolean).join(" ")}
                    colSpan={header.colSpan}
                    rowSpan={header.rowSpan}
                    scope={header.scope}
                    key={header.key}
                    onClick={header.sortable ? () => toggleSort(header.key) : undefined}
                  >
                    <span class="header-label">{header.label}</span>
                    {sortState?.key === header.key && sortState.direction ? (
                      <span class="sort-indicator">
                        {sortState.direction === "asc" ? "▲" : "▼"}
                      </span>
                    ) : null}
                  </th>
                ))}
              </tr>
            ) : null}
          </thead>
        ) : null}
        <tbody>
          {displayRows.map((row, rowIndex) => {
            const isDraftRow = Boolean(rowEditing?.appendEmptyRow) && !rowEditing.isRowComplete(row);

            return (
            <tr key={row.id} class={isDraftRow ? "draft-row" : ""}>
              {columns.map((column, colIndex) => {
                const isEditing = grid.editingCell?.row === rowIndex && grid.editingCell?.col === colIndex;
                const isCombobox = column.editor?.kind === "combobox";
                const isAutocomplete = column.editor?.kind === "autocomplete";

                return (
                  <td
                    class={grid.cellClasses(rowIndex, colIndex, column, row)}
                    data-col-index={colIndex}
                    data-row-index={rowIndex}
                    onClick={() => grid.activateCell(rowIndex, colIndex)}
                    onDblClick={() => grid.startEdit(rowIndex, colIndex, row)}
                    key={column.key}
                  >
                    {!isEditing && column.renderCell && (
                      <div class="display custom">{column.renderCell(row)}</div>
                    )}

                    {!isEditing && !column.renderCell && (
                      <div class="display">{column.displayValue(row)}</div>
                    )}

                    {isEditing && (
                      <div class="editor">
                        {isCombobox ? (
                          <DataGridComboboxEditor
                            column={column}
                            inputRef={grid.editorRef}
                            onCancel={grid.cancelEdit}
                            onChange={grid.setDraftValue}
                            onBlurCommit={(nextValue) => {
                              grid.setDraftValue(nextValue);
                              grid.handleEditorBlur(nextValue);
                            }}
                            onCommit={grid.commitEdit}
                            onTabForward={grid.commitEditAndMove ? (nextValue) => {
                              grid.commitEditAndMove("forward", nextValue);
                            } : undefined}
                            onTabBackward={grid.commitEditAndMove ? (nextValue) => {
                              grid.commitEditAndMove("backward", nextValue);
                            } : undefined}
                            row={row}
                            value={grid.draftValue}
                          />
                        ) : isAutocomplete ? (
                          <DataGridAutocompleteEditor
                            column={column}
                            inputRef={grid.editorRef}
                            onChange={grid.setDraftValue}
                            onBlurCommit={() => grid.handleEditorBlur()}
                            value={grid.draftValue}
                          />
                        ) : (
                          <input
                            type={column.inputType ?? "text"}
                            step={column.step}
                            min={column.min}
                            max={column.max}
                            value={grid.draftValue}
                            onInput={(event) => grid.setDraftValue(event.currentTarget.value)}
                            onKeyDown={(event) => {
                              if (column.inputType !== "number") {
                                return;
                              }

                              if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
                                return;
                              }

                              event.preventDefault();
                              const direction = event.key === "ArrowUp" ? 1 : -1;
                              grid.setDraftValue(stepNumericInputValue(grid.draftValue, direction, column));
                            }}
                            onMouseDown={(event) => {
                              event.stopPropagation();
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                              if (column.inputType === "date") {
                                event.currentTarget.showPicker?.();
                              }
                            }}
                            onBlur={() => grid.handleEditorBlur()}
                            ref={grid.editorRef}
                          />
                        )}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
